import { useEffect, useRef, useState, type ReactNode } from 'react';
import { addLevel, alignLevels, blankLevelCount, hasSharedLevels, removeLevel, renameLevel } from '../../core/editing';
import { DEFAULT_LEVEL_NAMES, MAX_ITEMS, MAX_LEVELS, MIN_LEVELS, validateScoreRequest, type Rubric, type RubricItem } from '../../core/rubric';
import { RUBRIC_LIBRARY, libraryIdOf } from '../../core/library';
import type { PasteResult, PasteSource } from '../../core/paste';
import ItemEditor from './ItemEditor';
import PasteRubric from './PasteRubric';

export interface RubricPaneProps {
  rubric: Rubric;
  onChange: (rubric: Rubric) => void;
  fullscreen: boolean;
  disabled?: boolean;
  onToggleFullscreen: () => void;
  newId: () => string;
  /** The teacher's own rubric, shown under Yours even while a template is in use. */
  mine?: Rubric | null;
  onLoadMine?: () => void;
  /** Replace the rubric with one from the library. */
  onLoadLibrary?: (rubricId: string) => void;
  /** A paste was read, successfully or not. */
  onPasted?: (source: PasteSource, result: PasteResult) => void;
  /** The Score control, rendered at the end of the strip. */
  scoreAction?: ReactNode;
  /** AI structuring, when the Worker has a key. */
  onStructure?: (text: string) => Promise<PasteResult | string>;
}


const blankItem = (id: string, template?: RubricItem): RubricItem => ({
  id, name: '', description: '',
  levels: (template?.levels.map(l => l.name) ?? DEFAULT_LEVEL_NAMES).map(name => ({ name, text: '' })),
});

export default function RubricPane({ rubric, onChange, fullscreen, onToggleFullscreen, newId, onLoadLibrary, onPasted, onStructure, scoreAction, mine, onLoadMine, disabled = false }: RubricPaneProps) {
  const [draft, setDraft] = useState(rubric);
  const [selection, setSelection] = useState('overview');
  const [error, setError] = useState('');
  const [removing, setRemoving] = useState<number | null>(null);
  const [pasting, setPasting] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const sidebar = useRef<HTMLElement>(null);
  // On a phone the structure is a swipeable row; keep the current chip in view.
  useEffect(() => {
    sidebar.current?.querySelector<HTMLElement>('[aria-current="page"]')?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [selection, fullscreen]);
  useEffect(() => {
    if (!fullscreen) return;
    const element = dialog.current;
    element?.showModal();
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { element?.close(); document.body.style.overflow = previous; };
  }, [fullscreen]);

  const open = (start?: Rubric) => {
    const next = start ?? structuredClone(rubric);
    setDraft(next);
    // A pasted rubric opens where it needs work: the first blank description, else the title.
    const blank = start && next.items.find(it => it.levels.some(l => !l.text.trim()));
    setSelection(blank ? blank.id : start ? 'overview' : next.items[0]?.id ?? 'overview');
    setError(''); setRemoving(null); setPasting(false); onToggleFullscreen();
  };
  const set = (patch: Partial<Rubric>) => setDraft({ ...draft, ...patch });
  const choose = (value: string) => { setSelection(value); setRemoving(null); };
  const index = draft.items.findIndex(item => item.id === selection);
  const item = draft.items[index];
  const levels = draft.items[0]?.levels ?? [];
  const shared = hasSharedLevels(draft);
  const libraryId = libraryIdOf(rubric);
  const blankLevels = blankLevelCount(rubric);
  const save = () => {
    if (!draft.title.trim()) { setError('Give your rubric a title.'); choose('overview'); return; }
    if (!shared) { setError('Align performance levels across criteria before saving.'); choose('levels'); return; }
    const unnamedLevel = levels.findIndex(l => !l.name.trim());
    if (unnamedLevel >= 0) { setError(`Name performance level ${unnamedLevel + 1}.`); choose('levels'); return; }
    const unnamed = draft.items.find(it => !it.name.trim());
    if (unnamed) { setError('Give each criterion a name.'); choose(unnamed.id); return; }
    const validation = validateScoreRequest({ text: 'Validation.', rubric: draft });
    if (!validation.ok) { setError('Check the rubric limits: up to 8 criteria, 2–6 levels, a 200-character title, and 2,000 characters per description.'); return; }
    onChange(draft); onToggleFullscreen();
  };
  const addItem = () => {
    const next = blankItem(newId(), draft.items[0]);
    set({ items: [...draft.items, next] }); choose(next.id);
  };

  if (!fullscreen) return (
    // Writing comes first; the rubric is one strip above it: what you grade with, its
    // criteria, and the actions on it. Score sits here so it is next to the rubric it uses.
    <section className="rubric-strip" aria-labelledby="rubric-heading">
      <h2 id="rubric-heading" className="visually-hidden">Rubric</h2>
      {/* Build with AI takes the whole strip while it is open; Cancel brings the strip back. */}
      {pasting && onStructure ? <PasteRubric onReview={open} onCancel={() => setPasting(false)} onStructure={onStructure} {...(onPasted ? { onParsed: onPasted } : {})} /> : <>
      <div className="strip-row">
        <label className="field strip-picker">
          <span>Grading with</span>
          <select value={libraryId ?? 'mine'} disabled={disabled} onChange={e => { if (e.target.value === 'mine') onLoadMine?.(); else onLoadLibrary?.(e.target.value); }}>
            {(libraryId === undefined || mine) && <optgroup label="Yours"><option value="mine">{(libraryId === undefined ? rubric.title : mine?.title) || 'My rubric'}</option></optgroup>}
            <optgroup label="Templates">
              {RUBRIC_LIBRARY.map(entry => <option key={entry.id} value={entry.id}>{entry.rubric.title} — {entry.note}</option>)}
            </optgroup>
          </select>
        </label>
        <div className="strip-actions">
          <button type="button" className="secondary strip-btn" disabled={disabled} onClick={() => open()}>Edit rubric</button>
          {onStructure && <button type="button" className="secondary strip-btn" disabled={disabled} onClick={() => setPasting(true)}>Build with AI <span className="ai-badge" aria-hidden="true">AI</span></button>}
          <button type="button" className="secondary strip-btn" disabled={disabled} onClick={() => open({ version: 1, title: 'My rubric', assignment: '', passLevel: 2, items: [blankItem(newId())] })}>Start blank</button>
        </div>
        {scoreAction && <span className="strip-score">{scoreAction}</span>}
      </div>
      <ul className="criteria-chips ph-mask" aria-label="Criteria">
        {rubric.items.map(it => <li key={it.id} title={it.description}>{it.name}</li>)}
      </ul>
      <p className="hint strip-facts">
        <span>{rubric.items.length} criteria · {rubric.items[0]?.levels.length ?? 0} performance levels</span>
        <span>Passing level: {rubric.items[0]?.levels[rubric.passLevel]?.name} or better</span>
        {blankLevels > 0 && <span className="blank-levels">{blankLevels} level description{blankLevels === 1 ? ' is' : 's are'} empty, so {blankLevels === 1 ? 'that level is' : 'those levels are'} scored by name alone. Add descriptions for sharper scores.</span>}
      </p>
      </>}
    </section>
  );

  return (
    <dialog ref={dialog} className="focused-editor" aria-labelledby="rubric-editor-heading" onCancel={e => { e.preventDefault(); onToggleFullscreen(); }}>
      <header className="editor-header"><div><p className="editor-eyebrow">Rubric workspace · Unsaved draft</p><h2 id="rubric-editor-heading" className="ph-mask">{draft.title || 'New rubric'}</h2></div>
        <div className="pane-actions"><button type="button" className="secondary" onClick={onToggleFullscreen}>Cancel</button><button type="button" className="primary" disabled={disabled} onClick={save}>Save rubric</button></div>
      </header>
      <fieldset className="rubric-fields focused-layout" disabled={disabled}>
        <nav ref={sidebar} className="editor-sidebar" aria-label="Rubric structure">
          <p className="editor-eyebrow">Rubric structure</p>
          <button type="button" aria-current={selection === 'overview' ? 'page' : undefined} onClick={() => choose('overview')}>Overview & assignment</button>
          <button type="button" aria-current={selection === 'levels' ? 'page' : undefined} onClick={() => choose('levels')}>Performance levels <span>{levels.length}</span></button>
          <p className="editor-eyebrow">Criteria · {draft.items.length}</p>
          {draft.items.map((it, i) => <button type="button" key={it.id} className="ph-mask" aria-current={it.id === selection ? 'page' : undefined} onClick={() => choose(it.id)}>{i + 1}. {it.name || 'Untitled criterion'}</button>)}
          <button type="button" className="add-criterion" disabled={draft.items.length >= MAX_ITEMS} onClick={addItem}>+ Add criterion</button>
        </nav>
        <div className="editor-content">
          {error && <p role="alert" className="message" data-tone="error">{error}</p>}
          {selection === 'overview' && <>
            <p className="editor-eyebrow">Rubric overview</p><h3>Set the context.</h3>
            <label className="field">Title<input className="ph-mask" maxLength={200} value={draft.title} onChange={e => set({ title: e.target.value })} /></label>
            <label className="field">Assignment and source material<textarea className="ph-mask" rows={8} value={draft.assignment ?? ''} onChange={e => set({ assignment: e.target.value })} /></label>
            <label className="field">Passing level<select className="ph-mask" value={draft.passLevel} onChange={e => set({ passLevel: Number(e.target.value) })}>{levels.map((l, i) => <option key={i} value={i}>{l.name} or better</option>)}</select></label>
            <p className="hint">A criterion is estimated as likely met when the writing seems to reach this level. It is an estimate, not a hard rating, grade or official exam passing score.</p>
          </>}
          {selection === 'levels' && <>
            <p className="editor-eyebrow">Shared across all {draft.items.length} criteria</p><h3>One scale for the whole rubric.</h3>
            <p className="hint">Add or rename levels here. Every criterion uses these names in the same order, from lowest to highest. Descriptions are written separately within each criterion.</p>
            {!shared && <div className="notice"><p>This saved rubric has different levels across criteria. Use the first criterion’s scale to align them. Existing descriptions stay at the same position; extra levels will be removed.</p><button type="button" className="secondary" onClick={() => setDraft(alignLevels(draft))}>Use first criterion’s scale</button></div>}
            <ol className="shared-levels">{levels.map((l, i) => <li key={i}><span className="level-number">{i + 1}</span><label className="field">Level {i + 1} name<input className="ph-mask" value={l.name} disabled={!shared} onChange={e => setDraft(renameLevel(draft, i, e.target.value))} /></label><button type="button" className="secondary small" disabled={!shared || levels.length <= MIN_LEVELS} aria-label={`Remove level ${i + 1}`} onClick={() => setRemoving(i)}>Remove</button></li>)}</ol>
            {removing !== null && <div className="notice" role="alert"><p>Remove “{levels[removing]?.name}” and its descriptions from all {draft.items.length} criteria? The passing level will adjust if needed. Canceling the draft restores these descriptions.</p><div className="pane-actions"><button type="button" className="secondary" onClick={() => setRemoving(null)}>Keep level</button><button type="button" className="primary" onClick={() => { setDraft(removeLevel(draft, removing)); setRemoving(null); }}>Remove from all criteria</button></div></div>}
            <button type="button" className="secondary" disabled={!shared || levels.length >= MAX_LEVELS} onClick={() => { setDraft(addLevel(draft)); setRemoving(null); }}>+ Add performance level</button>
            <p className="hint level-limit">{MIN_LEVELS}–{MAX_LEVELS} levels. A new level starts with a blank description in every criterion.</p>
          </>}
          {item && <ItemEditor item={item} index={index} count={draft.items.length} onManageLevels={() => choose('levels')}
            onChange={next => set({ items: draft.items.map(it => it.id === next.id ? next : it) })}
            onRemove={() => { const items = draft.items.filter(it => it.id !== item.id); set({ items }); choose(items[Math.max(0, index - 1)]?.id ?? 'overview'); }}
            onMove={direction => { const items = [...draft.items]; const target = index + direction; [items[index], items[target]] = [items[target]!, items[index]!]; set({ items }); }} />}
        </div>
      </fieldset>
      <footer className="editor-footer"><span>Changes apply when you save. Cancel keeps your current rubric.</span></footer>
    </dialog>
  );
}
